import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "../../api";
import { Button } from "../../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useToast } from "../../hooks/use-toast";
import { formatDate } from "../../lib/blog";
import { Plus, Pencil, Trash2, Loader2, ExternalLink } from "lucide-react";

export default function AdminArticles() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/admin/articles");
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async () => {
    try {
      await adminApi.delete(`/admin/articles/${deleteId}`);
      toast({ title: "Blog post deleted" });
      load();
    } catch {
      toast({ title: "Delete failed", variant: "destructive" });
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-[28px] text-[#0f1e33]">Blog</h1>
          <p className="text-muted-foreground mt-1">Write and publish blog posts. Drafts stay hidden from the website.</p>
        </div>
        <Button asChild className="bg-[#0e7490] hover:bg-[#0b5563] text-white h-11">
          <Link to="/admin/articles/new"><Plus className="h-4 w-4 mr-1.5" /> New post</Link>
        </Button>
      </div>

      <div className="mt-6 rounded-2xl border border-border bg-white overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : items.length === 0 ? (
          <p className="p-12 text-center text-muted-foreground">No blog posts yet. Click "New post" to write the first one.</p>
        ) : (
          <div className="divide-y divide-border">
            {items.map((a) => (
              <div key={a.id} className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#0f1e33] truncate">{a.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {a.category} · {a.published_at ? formatDate(a.published_at) : "No date"} · {a.read_minutes} min read
                  </p>
                </div>
                <span className={`w-fit text-xs font-medium px-2.5 py-1 rounded-full ${a.published ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {a.published ? "Published" : "Draft"}
                </span>
                <div className="flex items-center gap-2">
                  {a.published && (
                    <a href={`/blog/${a.slug}`} target="_blank" rel="noopener noreferrer" aria-label="View on site" className="h-9 w-9 rounded-lg border border-border flex items-center justify-center hover:bg-secondary">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                  <Link to={`/admin/articles/${a.id}`} aria-label="Edit" className="h-9 w-9 rounded-lg border border-border flex items-center justify-center hover:bg-secondary">
                    <Pencil className="h-4 w-4" />
                  </Link>
                  <button onClick={() => setDeleteId(a.id)} aria-label="Delete" className="h-9 w-9 rounded-lg border border-border flex items-center justify-center text-red-500 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this blog post?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes it from the website and cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
